import { Add, Remove } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { addTestSteps, editTestStep } from '../../../modules/teststep';
import Modal from '../../../ui/layout/Modal';

const EMPTY_SCRIPT = { step: '', testData: '', expectedResult: '', uri: '' };

const FIELD_CONFIG = [
  { id: 'step', label: 'Description', placeholder: 'Enter step description', required: true, maxLength: 2000 },
  { id: 'testData', label: 'Test Data', placeholder: 'Enter test data', maxLength: 2000 },
  { id: 'expectedResult', label: 'Expected Result', placeholder: 'Enter expected result', maxLength: 2000 },
];

const URI_FIELD = {
  id: 'uri', label: 'URI', placeholder: 'https://api.example.com/endpoint', maxLength: 250,
};
const colSpan = (fieldCount) => (fieldCount === 4 ? 3 : fieldCount === 3 ? 4 : 6);

// ─── Styled components (replace all inline sx on structural elements) ─────────

const StepCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: `color-mix(in srgb, ${theme.palette.primary.main} 1.5%, transparent)`,
  position: 'relative',
}));

const StepBadge = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StepBadgeLabel = styled(Typography)({
  color: '#fff',
  fontSize: '0.72rem',
  fontWeight: 700,
});

const StepRow = ({ script, index, stepNo, fields, mode, isLast, isOnly, onChange, onAdd, onRemove }) => (
  <StepCard>
    {/* Header row */}
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <StepBadge>
          <StepBadgeLabel>{stepNo}</StepBadgeLabel>
        </StepBadge>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Step {stepNo}
        </Typography>
      </Stack>

      {mode === 'add' && (
        <Stack direction="row" spacing={0.5}>
          {!isOnly && (
            <Tooltip title="Remove this step">
              <IconButton size="small" color="error" onClick={() => onRemove(index)}>
                <Remove fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isLast && (
            <Tooltip title="Add another step">
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={onAdd}>
                Add Step
              </Button>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>

    <Grid container spacing={2}>
      {fields.map((field) => (
        <Grid item key={field.id} xs={12} sm={colSpan(fields.length)}>
          <TextField
            fullWidth
            multiline
            rows={4}
            size="small"
            variant="outlined"
            label={field.label}
            placeholder={field.placeholder}
            value={script?.[field.id] ?? ''}
            onChange={(e) => onChange(field.id, e.target.value, index, field.id)}
            required={field.required}
            inputProps={{ maxLength: field.maxLength }}
            helperText={field.maxLength ? `${(script?.[field.id] ?? '').length}/${field.maxLength}` : undefined}
            FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
          />
        </Grid>
      ))}
    </Grid>
  </StepCard>
);

const TestStepsFormModal = ({
  show,
  handleClose,
  script,
  fetchScripts,
  testCaseId,
  addNotification,
  mode,
  totalScripts,
  technology_layer,
}) => {
  const [formData, setFormData] = useState({ scripts: [EMPTY_SCRIPT] });
  const [stepNos, setStepNos] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const fields = technology_layer === 'API/Integration'
    ? [...FIELD_CONFIG, URI_FIELD]
    : FIELD_CONFIG;

  useEffect(() => {
    if (mode === 'edit') {
      setFormData({ scripts: [script] });
      setStepNos(script?.sNo);
    } else {
      setFormData({ scripts: [{ ...EMPTY_SCRIPT }] });
      setStepNos(totalScripts + 1);
    }
  }, [script, mode, totalScripts]);

  const handleAddScript = () =>
    setFormData((prev) => ({ scripts: [...prev.scripts, { ...EMPTY_SCRIPT }] }));

  const handleRemoveScript = (index) =>
    setFormData((prev) => ({ scripts: prev.scripts.filter((_, i) => i !== index) }));

  const handleChange = (_id, value, index, field) =>
    setFormData((prev) => ({
      ...prev,
      scripts: prev.scripts.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));

  const buildPayload = () => ({
    testSteps: formData.scripts.map(({ id, step, uri, testData, expectedResult }) => ({
      id: id || null,
      step,
      testData,
      expectedResult,
      uri: uri || '',
    })),
  });

  const withSubmit = (asyncFn) => async () => {
    setSubmitting(true);
    try {
      await asyncFn();
      handleClose();
      fetchScripts();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = withSubmit(async () => {
    try {
      await addTestSteps(buildPayload(), testCaseId);
      addNotification('success', 'Test steps added successfully!');
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to add test steps. Please try again.');
      throw err;
    }
  });

  const handleEditSubmit = withSubmit(async () => {
    const step = formData.scripts.find((s) => s.id === script.id);
    const testStep = {
      ...step,
      step: step.step.trim(),
      testData: step.testData.trim(),
      expectedResult: step.expectedResult.trim(),
    };
    try {
      await editTestStep({ testStep }, script.id);
      addNotification('success', 'Test step updated successfully!');
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to update test step. Please try again.');
      throw err;
    }
  });

  const isSaveDisabled = submitting || formData.scripts.some((s) => !s?.step?.trim());

  return (
    <Modal
      open={show}
      width="xl"
      handleClose={handleClose}
      modalTitle={mode === 'edit' ? 'Edit Test Step' : 'Add Test Steps'}
      action={() => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="medium"
            disabled={isSaveDisabled}
            onClick={mode === 'edit' ? handleEditSubmit : handleSubmit}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outlined" color="secondary" size="medium" onClick={handleClose}>
            Cancel
          </Button>
        </Stack>
      )}
    >
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        {formData.scripts.map((s, idx) => (
          <StepRow
            key={idx}
            script={s}
            index={idx}
            stepNo={stepNos + idx}
            fields={fields}
            mode={mode}
            isLast={idx === formData.scripts.length - 1}
            isOnly={formData.scripts.length === 1}
            onChange={handleChange}
            onAdd={handleAddScript}
            onRemove={handleRemoveScript}
          />
        ))}
      </Stack>
    </Modal>
  );
};

export default TestStepsFormModal;